import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import { useTranslation } from 'react-i18next'
import {
  useVendorDecisionAttachmentsArchive,
  VendorDecisionAttachment
} from 'vendors/vendorUserRequestForProposals/vendorClosedProposalItem/api/useVendorDecisionAttachmentsApi'

/** Fixed row height (px) so the scroll cutoff lands exactly between rows. */
const ATTACHMENT_ROW_HEIGHT = 48
const MAX_VISIBLE_ATTACHMENTS = 4

const ProposalDownloadPopoverDecisionAttachments: React.FC<{
  contextToken: string
  attachments: VendorDecisionAttachment[]
}> = ({ contextToken, attachments }) => {
  const { t } = useTranslation()
  const { downloadAll, isDownloading } = useVendorDecisionAttachmentsArchive(
    contextToken,
    `${t('decision-attachments')}.zip`
  )

  if (attachments.length === 0) {
    return null
  }

  const handleDownload = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="overline" sx={{ px: 2, display: 'block', color: 'text.secondary' }}>
        {t('decision-attachments')}
      </Typography>
      <Box sx={{ maxHeight: ATTACHMENT_ROW_HEIGHT * MAX_VISIBLE_ATTACHMENTS, overflowY: 'auto' }}>
        {attachments.map((attachment) => (
          <MenuItem
            key={attachment.id}
            onClick={() => handleDownload(attachment.url)}
            aria-label={`${t('download-decision-attachment')} ${attachment.originalName}`}
            sx={{ height: ATTACHMENT_ROW_HEIGHT }}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography noWrap>{attachment.originalName}</Typography>
          </MenuItem>
        ))}
      </Box>
      {attachments.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
          <Button
            color="primary"
            onClick={downloadAll}
            disabled={isDownloading}
            startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('download-all-decision-attachments')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ProposalDownloadPopoverDecisionAttachments
